import React, { useEffect, useRef, useState } from 'react';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function loadGoogleMaps() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.google && window.google.maps && window.google.maps.places) {
    return Promise.resolve(window.google);
  }
  if (window.__gmapsLoadingPromise) {
    return window.__gmapsLoadingPromise;
  }
  window.__gmapsLoadingPromise = new Promise((resolve, reject) => {
    if (!API_KEY) {
      reject(new Error('Missing REACT_APP_GOOGLE_MAPS_API_KEY'));
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places&language=en&region=US&v=quarterly`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = (e) => reject(e);
    document.head.appendChild(script);
  });
  return window.__gmapsLoadingPromise;
}

// USPS Pub 28 — стандартные сокращения суффиксов улиц.
const STREET_SUFFIX_ABBR = {
  alley: 'Aly', avenue: 'Ave', boulevard: 'Blvd', bypass: 'Byp',
  causeway: 'Cswy', center: 'Ctr', circle: 'Cir', cliff: 'Clf',
  club: 'Clb', common: 'Cmn', corner: 'Cor', course: 'Crse',
  court: 'Ct', cove: 'Cv', creek: 'Crk', crescent: 'Cres',
  crossing: 'Xing', crossroad: 'Xrd', curve: 'Curv', drive: 'Dr',
  estate: 'Est', expressway: 'Expy', extension: 'Ext', fall: 'Fall',
  ferry: 'Fry', field: 'Fld', flat: 'Flt', ford: 'Frd',
  forest: 'Frst', forge: 'Frg', fork: 'Frk', fort: 'Ft',
  freeway: 'Fwy', garden: 'Gdn', gardens: 'Gdns', gateway: 'Gtwy',
  glen: 'Gln', green: 'Grn', grove: 'Grv', harbor: 'Hbr',
  haven: 'Hvn', heights: 'Hts', highway: 'Hwy', hill: 'Hl',
  hills: 'Hls', hollow: 'Holw', island: 'Is', junction: 'Jct',
  key: 'Ky', knoll: 'Knl', lake: 'Lk', lakes: 'Lks',
  landing: 'Lndg', lane: 'Ln', light: 'Lgt', loaf: 'Lf',
  lock: 'Lck', lodge: 'Ldg', loop: 'Loop', mall: 'Mall',
  manor: 'Mnr', meadow: 'Mdw', meadows: 'Mdws', mews: 'Mews',
  mill: 'Ml', mission: 'Msn', mount: 'Mt', mountain: 'Mtn',
  neck: 'Nck', orchard: 'Orch', overpass: 'Opas', park: 'Park',
  parkway: 'Pkwy', pass: 'Pass', passage: 'Psge', path: 'Path',
  pike: 'Pike', pine: 'Pne', place: 'Pl', plain: 'Pln',
  plaza: 'Plz', point: 'Pt', port: 'Prt', prairie: 'Pr',
  radial: 'Radl', ranch: 'Rnch', rapid: 'Rpd', rest: 'Rst',
  ridge: 'Rdg', river: 'Riv', road: 'Rd', route: 'Rte',
  row: 'Row', run: 'Run', shoal: 'Shl', shore: 'Shr',
  spring: 'Spg', square: 'Sq', station: 'Sta', stream: 'Strm',
  street: 'St', summit: 'Smt', terrace: 'Ter', throughway: 'Trwy',
  trace: 'Trce', track: 'Trak', trail: 'Trl', tunnel: 'Tunl',
  turnpike: 'Tpke', underpass: 'Upas', union: 'Un', valley: 'Vly',
  viaduct: 'Via', view: 'Vw', village: 'Vlg', ville: 'Vl',
  vista: 'Vis', walk: 'Walk', way: 'Way', well: 'Wl',
  wells: 'Wls',
};

// Стандартные двухсимвольные направления USPS.
const DIRECTIONAL_ABBR = {
  north: 'N', south: 'S', east: 'E', west: 'W',
  northeast: 'NE', northwest: 'NW', southeast: 'SE', southwest: 'SW',
};

// USPS Pub 28 — сокращения secondary unit designators (apt, ste, и т.д.).
// Ключи — long и уже-сокращённые варианты, чтобы не нормализовать дважды и не
// ломать корректный ввод от юзера.
const UNIT_DESIGNATOR_ABBR = {
  apartment: 'Apt', apt: 'Apt',
  basement: 'Bsmt', bsmt: 'Bsmt',
  building: 'Bldg', bldg: 'Bldg',
  department: 'Dept', dept: 'Dept',
  floor: 'Fl', fl: 'Fl',
  front: 'Frnt', frnt: 'Frnt',
  hangar: 'Hngr', hngr: 'Hngr',
  lobby: 'Lbby', lbby: 'Lbby',
  lot: 'Lot',
  lower: 'Lowr', lowr: 'Lowr',
  office: 'Ofc', ofc: 'Ofc',
  penthouse: 'Ph', ph: 'Ph',
  pier: 'Pier',
  rear: 'Rear',
  room: 'Rm', rm: 'Rm',
  side: 'Side',
  slip: 'Slip',
  space: 'Spc', spc: 'Spc',
  stop: 'Stop',
  suite: 'Ste', ste: 'Ste',
  trailer: 'Trlr', trlr: 'Trlr',
  unit: 'Unit',
  upper: 'Uppr', uppr: 'Uppr',
};

function abbreviateStreet(street) {
  if (!street) return street;
  const words = street.trim().split(/\s+/);
  if (words.length < 2) return street;
  return words
    .map((word, idx) => {
      const stripped = word.replace(/[.,]/g, '');
      const lower = stripped.toLowerCase();
      // Направления — сокращаем где угодно (часто это первое или последнее слово).
      if (DIRECTIONAL_ABBR[lower]) return DIRECTIONAL_ABBR[lower];
      // Суффикс — сокращаем только если это не первое слово (первое — обычно номер
      // или название), чтобы не ломать улицы вида "Park Avenue" (Park остаётся именем).
      if (idx > 0 && STREET_SUFFIX_ABBR[lower]) return STREET_SUFFIX_ABBR[lower];
      // Unit designator (apt/ste/unit/...) — только не первым словом.
      if (idx > 0 && UNIT_DESIGNATOR_ABBR[lower]) return UNIT_DESIGNATOR_ABBR[lower];
      return word;
    })
    .join(' ');
}

function parsePlace(place) {
  const comps = place.address_components || [];
  const get = (type, short = false) => {
    const c = comps.find((cc) => cc.types.includes(type));
    if (!c) return '';
    return short ? c.short_name : c.long_name;
  };
  const streetNumber = get('street_number');
  const route = get('route');
  // subpremise — apartment/suite/unit/floor, если Google его выделил отдельно.
  const subpremise = get('subpremise');
  let streetAddress = [streetNumber, route, subpremise].filter(Boolean).join(' ').trim();

  // Фоллбэк: если в компонентах нет street_number/route (бывает для общих
  // мест), берём первый сегмент formatted_address до первой запятой.
  if (!streetAddress && place.formatted_address) {
    streetAddress = String(place.formatted_address).split(',')[0].trim();
  } else if (!subpremise && place.formatted_address && streetAddress) {
    // Google не всегда выделяет apt/ste в address_components, но почти всегда
    // оставляет их в первом сегменте formatted_address. Если formatted-версия
    // начинается с того, что мы собрали из components, но длиннее — берём её,
    // чтобы не потерять "Ste 200" / "Apt 4B" и т.п.
    const formattedFirst = String(place.formatted_address).split(',')[0].trim();
    const lowerBuilt = streetAddress.toLowerCase();
    const lowerFormatted = formattedFirst.toLowerCase();
    if (
      formattedFirst.length > streetAddress.length &&
      lowerFormatted.startsWith(lowerBuilt)
    ) {
      streetAddress = formattedFirst;
    }
  }
  streetAddress = abbreviateStreet(streetAddress);
  const city =
    get('locality') ||
    get('sublocality') ||
    get('sublocality_level_1') ||
    get('postal_town') ||
    get('administrative_area_level_3') ||
    '';
  const state = get('administrative_area_level_1', true); // двухбуквенный код
  const zip = get('postal_code');
  const country = get('country', true);
  return { streetAddress, city, state, zip, country };
}

/**
 * Wraps a controlled <input> with Google Places Autocomplete.
 * On user selection, calls onAddressSelected({ streetAddress, city, state, zip, country }).
 *
 * Props:
 *   value, onChange — стандартные пропсы input'а (родитель сам управляет state)
 *   onAddressSelected — колбэк когда юзер выбрал вариант из дропдауна
 *   inputProps — всё остальное, что прокидывается на <input>
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onAddressSelected,
  inputProps = {},
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  // onAddressSelected пересоздаётся родителем на каждом ререндере. Держим её в
  // ref'е, чтобы эффект ниже зависел только от `ready` и не пересоздавал
  // Autocomplete (и не сносил pac-container) на каждое нажатие клавиши.
  const onAddressSelectedRef = useRef(onAddressSelected);
  useEffect(() => {
    onAddressSelectedRef.current = onAddressSelected;
  }, [onAddressSelected]);

  const [ready, setReady] = useState(
    !!(window.google && window.google.maps && window.google.maps.places)
  );

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch((err) => {
        console.error('Google Maps failed to load:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || !inputRef.current || autocompleteRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'us' },
      fields: ['address_components', 'formatted_address'],
      types: ['address'],
    });
    autocompleteRef.current = ac;
    const listener = ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (!place || !place.address_components) return;
      const parsed = parsePlace(place);
      const cb = onAddressSelectedRef.current;
      if (typeof cb === 'function') {
        cb(parsed);
      }
    });
    // Если скрипт Google Maps догрузился ПОСЛЕ того, как юзер уже успел кликнуть
    // в поле и что-то ввести — Autocomplete не покажет подсказки, пока юзер не
    // расфокусирует/вернёт фокус. Пинаем его синтетическим input-евентом, чтобы
    // он подхватил текущее значение сразу.
    if (inputRef.current.value && document.activeElement === inputRef.current) {
      inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }
    return () => {
      if (listener && window.google) {
        window.google.maps.event.removeListener(listener);
      }
      if (window.google && window.google.maps && autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
      autocompleteRef.current = null;
      // Google рисует выпадающий список в <div class="pac-container"> на <body>.
      // При размонтировании инпута этот div остаётся висеть — убираем вручную.
      document.querySelectorAll('.pac-container').forEach((el) => el.remove());
    };
  }, [ready]);

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={onChange}
      autoComplete="off"
      {...inputProps}
    />
  );
}
